import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FaEye, FaHandPointer, FaMagic, FaUsers, FaCheck, FaTimes } from 'react-icons/fa';
import { apiRequest } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import Pagination from '../components/Pagination';
import NotificationCsvImport from '../components/NotificationCsvImport';
import RecipientAutocomplete from '../components/RecipientAutocomplete';
import { CsvImportReport } from '../utils/notificationCsv';

type NotificationType = 'new_follower' | 'new_extension' | 'extension_recommendation' | 'user_suggestion' | 'announcement' | 'broadcast' | 'extension_promotion' | 'article' | 'generation_complete' | 'generation_failed' | 'custom_message' | 'inactive_reminder';

interface Notification {
  _id: string;
  userId: string;
  userName?: string;
  type: NotificationType;
  actorId?: string;
  actorName?: string;
  actorProfilePicture?: string;
  extensionId?: string;
  extensionThumbnail?: string;
  extensionName?: string;
  creatorId?: string;
  creatorName?: string;
  title?: string;
  message?: string;
  broadcastId?: string;
  broadcastCount?: number;
  campaignType?: NotificationType;
  audienceMode?: string;
  campaign?: Campaign;
  isRead: boolean;
  createdAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface UserSearchResult {
  userId: string;
  name: string;
  email?: string;
  avatar?: string;
}

interface ExtensionSearchResult {
  _id: string;
  Name: string;
  Creator: string;
  creatorName?: string;
  thumbnailImageUrl?: string;
  usageCount?: number;
}

// A published article option for the "Article" notification picker
// (GET /api/admin/notifications/articles).
interface ArticleOption {
  articleId: string;
  title: string;
  thumbnailUrl?: string;
}

interface Campaign {
  campaignId: string;
  campaignType: NotificationType;
  audienceMode: 'all' | 'specific' | 'inactive' | 'csv';
  inactiveDays?: number;
  title: string;
  message: string;
  remark?: string;
  csvImport?: { rowCount: number; duplicateRows: number };
  target?: string;
  expiresAt?: string;
  status: 'draft' | 'processing' | 'completed' | 'partial_failure' | 'failed' | 'unknown';
  error?: string;
  counts: {
    selectedUsers: number; eligibleUsers: number; skippedUsers: number;
    noActiveDevice: number; preferenceDisabled: number; eligibleTokens: number;
    acceptedTokens: number; failedTokens: number;
  };
}

// Engagement metrics for one broadcast, from
// GET /api/admin/notifications/broadcast/:broadcastId/engagement
interface BroadcastEngagement {
  recipients: number;
  readCount: number;
  interactedCount: number;
  usedCount: number;
  readRate: number;
  interactionRate: number;
  usageRate: number;
  usageOfInteractedRate: number;
  approx: boolean;
}

// One recipient of a broadcast, with per-user engagement flags, for the
// "view recipients" modal grid (GET .../broadcast/:broadcastId/recipients).
interface BroadcastRecipient {
  userId: string;
  userName: string;
  avatarUrl: string | null;
  isRead: boolean;
  tapped: boolean;
  used: boolean;
}

interface BroadcastRecipientsMeta {
  isExtensionPromotion: boolean;
  extensionName: string | null;
  totalRecipients: number;
  capped: boolean;
  approx: boolean;
}

const formatPct = (rate: number): string => `${(rate * 100).toFixed(1)}%`;

// Engagement (read / tapped / used) is tracked for any broadcast that points at
// an extension: admin promotions (persisted as extension_recommendation) and
// new-extension fan-outs to a creator's followers, which are grouped under one
// broadcastId per publish and carry the extensionId the same way.
const isPromotionRow = (n: { type: NotificationType; broadcastId?: string }): boolean =>
  !!n.broadcastId &&
  (n.type === 'extension_recommendation' ||
    n.type === 'extension_promotion' ||
    n.type === 'new_extension');

// Broadcast/campaign types offered in the standard "Add Notification" composer.
// Preserve existing choices; Custom Message is an additional campaign type.
const STANDARD_NOTIFICATION_TYPES: { value: NotificationType; label: string }[] = [
  { value: 'broadcast', label: 'Broadcast' },
  { value: 'extension_promotion', label: 'Lens Promotion' },
  { value: 'article', label: 'Article' },
  { value: 'custom_message', label: 'Custom Message' },
];

// Activity-driven types that normally fire from real user actions (a follow, a
// new extension, etc.). The admin can trigger them manually from the dedicated
// "Trigger Demo" button to test the notification + push pipeline end-to-end.
const DEMO_NOTIFICATION_TYPES: { value: NotificationType; label: string }[] = [
  { value: 'new_follower', label: 'New Follower' },
  { value: 'new_extension', label: 'New Lens' },
  { value: 'extension_recommendation', label: 'Lens Recommendation' },
  { value: 'user_suggestion', label: 'User Suggestion' },
];

// All real notification types — used by the list type-filter and getTypeLabel.
const NOTIFICATION_TYPES: { value: NotificationType; label: string }[] = [
  ...STANDARD_NOTIFICATION_TYPES,
  ...DEMO_NOTIFICATION_TYPES,
  { value: 'inactive_reminder', label: 'Re-engage Inactive Users' },
  { value: 'announcement', label: 'Announcement (legacy)' },
  { value: 'generation_complete', label: 'Generation Complete' },
  { value: 'generation_failed', label: 'Generation Failed' },
];

type ComposerType = NotificationType;
const COMPOSER_TYPES = STANDARD_NOTIFICATION_TYPES;

// Labels for types that may still appear in the list but are no longer offered
// in the composer (e.g. historical Announcement records).
const LEGACY_TYPE_LABELS: Record<string, string> = {
  announcement: 'Announcement',
};

const getTypeLabel = (type: NotificationType): string => {
  return NOTIFICATION_TYPES.find(t => t.value === type)?.label ?? LEGACY_TYPE_LABELS[type] ?? type;
};

// The copy an admin-composed notification was sent with is stored on the doc
// (title/message). The activity-driven types carry no copy at all — their push
// text is generated at send time from the backend i18n catalog — so the list
// rebuilds it from the same templates to show what the user actually received.
//
// Mirrors the `notification.*` block of Backend/src/i18n/locales/en.ts (services
// never share code, so this is a deliberate copy — keep the two in sync). Pushes
// are localized per recipient's preferred language; the list shows the English
// source copy. An undefined title means the push used the bare app name ("FRAW"),
// which carries no information, so the row renders the message alone.
const buildTemplateCopy = (n: Notification): { title?: string; message: string } => {
  switch (n.type) {
    case 'new_follower':
      return {
        title: "You've got new follower",
        message: n.actorName ? `${n.actorName} started following you` : 'You have a new follower',
      };
    case 'new_extension': {
      const creator = n.creatorName || 'Someone you follow';
      return {
        title: 'New Lens:',
        message: n.extensionName
          ? `${creator} just released a new lens: ${n.extensionName}`
          : `${creator} just released a new lens`,
      };
    }
    case 'extension_recommendation':
    case 'extension_promotion':
      return {
        message: n.extensionName
          ? `Recommended for you: ${n.extensionName}`
          : 'We have a lens recommendation for you',
      };
    case 'user_suggestion':
      return {
        message: n.actorName ? `You might want to follow ${n.actorName}` : 'Check out this creator',
      };
    default:
      return { message: 'You have a new notification' };
  }
};

// What to render in the unified Notification column: the stored copy when the
// admin authored it, otherwise the reconstructed template copy. A doc with a
// message but no stored title (e.g. an inactive-user reminder, whose title was
// resolved per-recipient at send time and never persisted) shows the message
// alone rather than a fabricated title.
const describeNotification = (n: Notification): { title?: string; message: string } =>
  n.title || n.message ? { title: n.title, message: n.message ?? '' } : buildTemplateCopy(n);

const getTypeBadgeColor = (type: NotificationType): string => {
  switch (type) {
    case 'new_follower': return 'var(--color-accent-alt)';
    case 'new_extension': return '#27ae60';
    case 'extension_recommendation': return '#9b59b6';
    case 'user_suggestion': return '#e67e22';
    case 'announcement': return '#2980b9';
    case 'broadcast': return '#e74c3c';
    case 'custom_message': return 'var(--color-accent-strong)';
    case 'inactive_reminder': return 'var(--color-warning-strong)';
    case 'extension_promotion': return '#d35400';
    case 'article': return '#16a085';
    default: return '#95a5a6';
  }
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  // The composer modal is shared between the standard campaign types and the
  // manual demo triggers; this flag decides which type set + labels it shows.
  const [composerMode, setComposerMode] = useState<'standard' | 'demo'>('standard');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [formType, setFormType] = useState<ComposerType>('broadcast');
  const [targetUserId, setTargetUserId] = useState('');
  const [actorId, setActorId] = useState('');
  const [extensionId, setExtensionId] = useState('');
  const [creatorId, setCreatorId] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [remark, setRemark] = useState('');
  const [csvReport, setCsvReport] = useState<CsvImportReport | null>(null);
  const [csvBusy, setCsvBusy] = useState(false);

  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [actorSearchQuery, setActorSearchQuery] = useState('');
  const [actorSearchResults, setActorSearchResults] = useState<UserSearchResult[]>([]);
  const [creatorSearchQuery, setCreatorSearchQuery] = useState('');
  const [, setCreatorSearchResults] = useState<UserSearchResult[]>([]);
  const [extensionSearchQuery, setExtensionSearchQuery] = useState('');
  const [extensionSearchResults, setExtensionSearchResults] = useState<ExtensionSearchResult[]>([]);

  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [selectedActor, setSelectedActor] = useState<UserSearchResult | null>(null);
  const [, setSelectedCreator] = useState<UserSearchResult | null>(null);
  const [selectedExtension, setSelectedExtension] = useState<ExtensionSearchResult | null>(null);

  // Campaign audience is independent of the content type.
  const [recipientMode, setRecipientMode] = useState<'all' | 'specific' | 'inactive' | 'csv'>('specific');
  const [selectedRecipients, setSelectedRecipients] = useState<UserSearchResult[]>([]);

  useEffect(() => { setCsvReport(null); setCsvBusy(false); }, [recipientMode]);

  // Top trending extensions for the extension_promotion broadcast picker
  const [trendingExtensions, setTrendingExtensions] = useState<ExtensionSearchResult[]>([]);
  const [trendingLoading, setTrendingLoading] = useState(false);

  // Published articles for the "Article" notification picker
  const [articles, setArticles] = useState<ArticleOption[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articleSearchQuery, setArticleSearchQuery] = useState('');
  const [articleId, setArticleId] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<ArticleOption | null>(null);

  // "View recipients" modal state — replaces the old inline accordion.
  const [recipientsBroadcast, setRecipientsBroadcast] = useState<Notification | null>(null);
  const [recipients, setRecipients] = useState<BroadcastRecipient[]>([]);
  const [recipientsMeta, setRecipientsMeta] = useState<BroadcastRecipientsMeta | null>(null);
  const [recipientsPagination, setRecipientsPagination] = useState<PaginationInfo | null>(null);
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  // Per-campaign engagement (interaction + usage), lazily fetched for the
  // extension-promotion rows on the current page.
  const [engagement, setEngagement] = useState<Record<string, BroadcastEngagement>>({});

  const [inactiveDays, setInactiveDays] = useState(7);
  const [preview, setPreview] = useState<Campaign | null>(null);
  const [sendAttempted, setSendAttempted] = useState(false);
  const [sendResult, setSendResult] = useState<Campaign | null>(null);
  const submitLock = useRef(false);
  const composerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (composerRef.current) composerRef.current.scrollTop = 0;
  }, [preview, showModal]);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '50',
        ...(filterType !== 'all' && { type: filterType })
      });

      const response = await apiRequest<{
        success: boolean;
        data: Notification[];
        pagination: PaginationInfo;
        error?: string;
      }>(`/api/admin/notifications?${params}`);

      if (response.success) {
        setNotifications(response.data);
        setPagination(response.pagination);
      } else {
        setError(response.error || 'Failed to fetch notifications');
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterType]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!notifications.some(n => n.campaign?.status === 'processing') && sendResult?.status !== 'processing') return;
    const timer = window.setInterval(() => {
      fetchNotifications();
      if (sendResult?.status === 'processing') {
        apiRequest<{ success: boolean; campaign: Campaign }>(`/api/admin/notifications/campaigns/${sendResult.campaignId}`)
          .then(result => { if (result.success) setSendResult(result.campaign); })
          .catch(() => undefined);
      }
    }, 4000);
    return () => window.clearInterval(timer);
  }, [notifications, sendResult, fetchNotifications]);

  // Lazily load engagement for the extension-linked broadcasts visible on the
  // current page (skips rows already fetched). Runs in parallel; failures leave
  // the row without stats rather than blocking the list.
  useEffect(() => {
    const pending = notifications.filter(
      (n) => isPromotionRow(n) && n.broadcastId && !(n.broadcastId in engagement)
    );
    if (pending.length === 0) return;

    let cancelled = false;
    Promise.all(
      pending.map(async (n) => {
        try {
          const res = await apiRequest<{ success: boolean } & BroadcastEngagement>(
            `/api/admin/notifications/broadcast/${n.broadcastId}/engagement`
          );
          if (res.success) return [n.broadcastId as string, res] as const;
        } catch (err) {
          console.error('Error fetching broadcast engagement:', err);
        }
        return null;
      })
    ).then((results) => {
      if (cancelled) return;
      const next: Record<string, BroadcastEngagement> = {};
      for (const r of results) {
        if (r) {
          const [id, res] = r;
          next[id] = {
            recipients: res.recipients,
            readCount: res.readCount,
            interactedCount: res.interactedCount,
            usedCount: res.usedCount,
            readRate: res.readRate,
            interactionRate: res.interactionRate,
            usageRate: res.usageRate,
            usageOfInteractedRate: res.usageOfInteractedRate,
            approx: res.approx,
          };
        }
      }
      if (Object.keys(next).length > 0) setEngagement((prev) => ({ ...prev, ...next }));
    });

    return () => {
      cancelled = true;
    };
  }, [notifications, engagement]);

  const fetchRecipients = useCallback(async (broadcastId: string, page: number) => {
    setRecipientsLoading(true);
    try {
      const response = await apiRequest<{
        success: boolean;
        data: BroadcastRecipient[];
        meta: BroadcastRecipientsMeta;
        pagination: PaginationInfo;
      }>(`/api/admin/notifications/broadcast/${broadcastId}/recipients?page=${page}&limit=60`);
      if (response.success) {
        setRecipients(response.data);
        setRecipientsMeta(response.meta);
        setRecipientsPagination(response.pagination);
      }
    } catch (err) {
      console.error('Error fetching broadcast recipients:', err);
    } finally {
      setRecipientsLoading(false);
    }
  }, []);

  const openRecipientsModal = (notification: Notification) => {
    if (!notification.broadcastId) return;
    setRecipientsBroadcast(notification);
    setRecipients([]);
    setRecipientsMeta(null);
    setRecipientsPagination(null);
    setRecipientsPage(1);
    fetchRecipients(notification.broadcastId, 1);
  };

  const changeRecipientsPage = (page: number) => {
    if (!recipientsBroadcast?.broadcastId) return;
    setRecipientsPage(page);
    fetchRecipients(recipientsBroadcast.broadcastId, page);
  };

  const closeRecipientsModal = () => {
    setRecipientsBroadcast(null);
  };

  const searchUsers = async (query: string, setter: (results: UserSearchResult[]) => void) => {
    if (query.length < 2) {
      setter([]);
      return;
    }
    try {
      const response = await apiRequest<{ success: boolean; data: UserSearchResult[] }>(
        `/api/admin/notifications/search-users?q=${encodeURIComponent(query)}`
      );
      if (response.success) {
        setter(response.data);
      }
    } catch (err) {
      console.error('Error searching users:', err);
    }
  };

  const searchExtensions = async (query: string) => {
    if (query.length < 2) {
      setExtensionSearchResults([]);
      return;
    }
    try {
      const response = await apiRequest<{ success: boolean; data: ExtensionSearchResult[] }>(
        `/api/admin/notifications/search-extensions?q=${encodeURIComponent(query)}`
      );
      if (response.success) {
        setExtensionSearchResults(response.data);
      }
    } catch (err) {
      console.error('Error searching extensions:', err);
    }
  };

  useEffect(() => {
    let active = true;
    setUserSearchResults([]);
    const timer = setTimeout(async () => {
      await searchUsers(userSearchQuery.trim(), results => { if (active) setUserSearchResults(results); });
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [userSearchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => searchUsers(actorSearchQuery, setActorSearchResults), 300);
    return () => clearTimeout(timer);
  }, [actorSearchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => searchUsers(creatorSearchQuery, setCreatorSearchResults), 300);
    return () => clearTimeout(timer);
  }, [creatorSearchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => searchExtensions(extensionSearchQuery), 300);
    return () => clearTimeout(timer);
  }, [extensionSearchQuery]);

  // Load / filter the article picker while composing an Article notification.
  // Fires on switching to the Article type (query '' → recent list) and on each
  // debounced title search.
  useEffect(() => {
    if (formType !== 'article') return;
    const timer = setTimeout(() => fetchArticles(articleSearchQuery), 300);
    return () => clearTimeout(timer);
  }, [articleSearchQuery, formType]);

  const fetchArticles = async (query: string = '') => {
    setArticlesLoading(true);
    try {
      const response = await apiRequest<{ success: boolean; data: ArticleOption[] }>(
        `/api/admin/notifications/articles${query ? `?q=${encodeURIComponent(query)}` : ''}`
      );
      if (response.success) {
        setArticles(response.data);
      }
    } catch (err) {
      console.error('Error fetching articles:', err);
    } finally {
      setArticlesLoading(false);
    }
  };

  const fetchTrendingExtensions = async () => {
    setTrendingLoading(true);
    try {
      const response = await apiRequest<{ success: boolean; data: ExtensionSearchResult[] }>(
        '/api/admin/notifications/trending-extensions?limit=10'
      );
      if (response.success) {
        setTrendingExtensions(response.data);
      }
    } catch (err) {
      console.error('Error fetching trending extensions:', err);
    } finally {
      setTrendingLoading(false);
    }
  };

  const handleDelete = async (id: string, broadcastId?: string) => {
    const confirmMsg = broadcastId
      ? 'This will delete ALL notifications in this broadcast. Are you sure?'
      : 'Are you sure you want to delete this notification?';
    if (!window.confirm(confirmMsg)) return;

    try {
      const response = await apiRequest<{ success: boolean; error?: string }>(
        `/api/admin/notifications/${id}`,
        { method: 'DELETE' }
      );

      if (response.success) {
        fetchNotifications();
      } else {
        setError(response.error || 'Failed to delete notification');
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete notification');
    }
  };

  const resetForm = () => {
    setFormType('broadcast');
    setTargetUserId('');
    setActorId('');
    setExtensionId('');
    setCreatorId('');
    setMessage('');
    setNotificationTitle('');
    setRemark('');
    setCsvReport(null);
    setCsvBusy(false);
    setUserSearchQuery('');
    setActorSearchQuery('');
    setCreatorSearchQuery('');
    setExtensionSearchQuery('');
    setSelectedUser(null);
    setSelectedActor(null);
    setSelectedCreator(null);
    setSelectedExtension(null);
    setArticleId('');
    setSelectedArticle(null);
    setArticleSearchQuery('');
    setArticles([]);
    setRecipientMode('specific');
    setPreview(null);
    setSendAttempted(false);
    setError(null);
    setSelectedRecipients([]);
    setUserSearchResults([]);
    setActorSearchResults([]);
    setCreatorSearchResults([]);
    setExtensionSearchResults([]);
    setInactiveDays(7);
  };

  // Escape closes the composer or the recipients list, matching their × buttons.
  useEscapeToClose(showModal && !submitting, () => { setShowModal(false); resetForm(); });
  useEscapeToClose(!!recipientsBroadcast, closeRecipientsModal);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isBroadcastType = STANDARD_NOTIFICATION_TYPES.some(option => option.value === formType);
    // A campaign preview fixes the audience before its send can be claimed.
    const isTargeted = isBroadcastType && recipientMode === 'specific';

    if (!isBroadcastType && !targetUserId) {
      setError('Please select a target user');
      return;
    }

    if (formType === 'extension_promotion' && !extensionId) {
      setError('Please select a lens to promote');
      return;
    }

    if (formType === 'article' && !articleId) {
      setError('Please select an article');
      return;
    }

    if (isTargeted && selectedRecipients.length === 0) {
      setError('Please select at least one recipient');
      return;
    }

    if (isBroadcastType && recipientMode === 'csv' && (!csvReport?.valid || !csvReport.importId || csvBusy)) {
      setError('Import a valid CSV and resolve all errors before previewing');
      return;
    }

    if (isBroadcastType && !message.trim()) {
      setError('Please enter a message for the notification');
      return;
    }

    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    setError(null);

    try {
      const payload: Record<string, unknown> = {
        type: formType
      };

      if (!isBroadcastType) {
        payload.userId = targetUserId;
      }

      if (isTargeted) {
        payload.userIds = selectedRecipients.map(u => u.userId);
      }

      if (actorId) payload.actorId = actorId;
      if (extensionId) payload.extensionId = extensionId;
      if (articleId) payload.articleId = articleId;
      if (creatorId) payload.creatorId = creatorId;
      if (message) payload.message = message;
      if (notificationTitle) payload.title = notificationTitle;

      if (isBroadcastType) {
        if (!preview) {
          payload.audienceMode = recipientMode;
          if (recipientMode === 'inactive') payload.inactiveDays = inactiveDays;
          if (recipientMode === 'csv') payload.csvImportId = csvReport?.importId;
          payload.remark = remark;
          const result = await apiRequest<{ success: boolean; preview: Campaign }>('/api/admin/notifications/preview', {
            method: 'POST', body: JSON.stringify(payload),
          });
          if (result.success) setPreview(result.preview);
        } else {
          setSendAttempted(true);
          const result = await apiRequest<{ success: boolean; campaign: Campaign }>('/api/admin/notifications', {
            method: 'POST', body: JSON.stringify({ campaignId: preview.campaignId }),
          });
          if (result.success) {
            setSendResult(result.campaign);
            setShowModal(false);
            resetForm();
            fetchNotifications();
          }
        }
        return;
      }
      const response = await apiRequest<{ success: boolean; error?: string }>(
        '/api/admin/notifications', { method: 'POST', body: JSON.stringify(payload) }
      );

      if (response.success) {
        setShowModal(false);
        resetForm();
        fetchNotifications();
      } else {
        setError(response.error || 'Failed to create notification');
      }
    } catch (err) {
      console.error('Error creating notification:', err);
      setError(err instanceof Error ? err.message : 'Failed to create notification');
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Used by both the trending list and the free-text search picker below it.
  const selectArticle = (a: ArticleOption) => {
    setSelectedArticle(a);
    setArticleId(a.articleId);
    // Prefill editable default copy referencing the chosen article
    if (!notificationTitle) setNotificationTitle('New on FRAW 📖');
    if (!message) setMessage(`Check out "${a.title}" on FRAW!`);
  };

  const selectPromotionExtension = (ext: ExtensionSearchResult) => {
    setSelectedExtension(ext);
    setExtensionId(ext._id);
    // Clear the search box + results so the picker collapses to the selection
    setExtensionSearchQuery('');
    setExtensionSearchResults([]);
    // Prefill an editable default message referencing the chosen extension
    setMessage(`Try "${ext.Name}" — trending now on FRAW! ✨`);
    if (!notificationTitle) setNotificationTitle('Trending on FRAW ✨');
  };

  // Shared audience picker for Custom Message, Lens Promotion and Article.
  const renderAudienceSelector = () => (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
        Audience *
      </label>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: recipientMode === 'specific' ? '12px' : 0 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--color-text)', fontSize: '14px' }}>
          <input
            type="radio"
            name="recipientMode"
            checked={recipientMode === 'all'}
            onChange={() => setRecipientMode('all')}
          />
          All users
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--color-text)', fontSize: '14px' }}>
          <input
            type="radio"
            name="recipientMode"
            checked={recipientMode === 'specific'}
            onChange={() => setRecipientMode('specific')}
          />
          Specific users
        </label>
        {formType === 'custom_message' && <>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--color-text)', fontSize: '14px' }}>
          <input type="radio" name="recipientMode" checked={recipientMode === 'inactive'} onChange={() => setRecipientMode('inactive')} />
          Inactive users
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--color-text)', fontSize: '14px' }}>
          <input type="radio" name="recipientMode" checked={recipientMode === 'csv'} onChange={() => setRecipientMode('csv')} />
          CSV import
        </label>
        </>}
      </div>
      {formType === 'custom_message' && recipientMode === 'csv' && <NotificationCsvImport onReport={setCsvReport} onBusyChange={setCsvBusy} />}
      {formType === 'custom_message' && recipientMode === 'inactive' && (
        <label style={{ display: 'block', color: 'var(--color-text)' }}>
          Inactive for at least (days)
          <input aria-label="Inactive days" type="number" min={1} max={3650} value={inactiveDays} onChange={e => setInactiveDays(Number(e.target.value))} style={inputStyle} />
          <small>Excludes internal accounts. The preview will show matching users and push eligibility.</small>
        </label>
      )}

      {recipientMode === 'specific' && (
        <RecipientAutocomplete value={selectedRecipients} onChange={users => { setSelectedRecipients(users); setError(null); }} />
      )}
    </div>
  );

  const renderTypeFields = () => {
    // Extension Promotion: pick a top-trending extension to broadcast + deep-link
    if (formType === 'extension_promotion') {
      return (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
              Lens to Promote * <span style={{ fontWeight: 400, color: 'var(--color-muted)' }}>(pick from top trending, or search for any lens below)</span>
            </label>
            {trendingLoading ? (
              <div style={{ color: 'var(--color-muted)', fontSize: '13px', padding: '12px 0' }}>Loading lenses…</div>
            ) : trendingExtensions.length === 0 ? (
              <div style={{ color: 'var(--color-muted)', fontSize: '13px', padding: '12px 0' }}>
                No trending lenses — use the search below to pick one.
              </div>
            ) : (
              <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '6px' }}>
                {trendingExtensions.map((ext, idx) => {
                  const isSelected = selectedExtension?._id === ext._id;
                  return (
                    <div
                      key={ext._id}
                      onClick={() => selectPromotionExtension(ext)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: idx < trendingExtensions.length - 1 ? '1px solid var(--color-surface-hover)' : 'none',
                        backgroundColor: isSelected ? 'var(--color-accent-surface)' : 'transparent',
                      }}
                    >
                      <span style={{ width: '22px', color: 'var(--color-muted)', fontSize: '13px', fontWeight: 600 }}>{idx + 1}</span>
                      {ext.thumbnailImageUrl ? (
                        <img src={ext.thumbnailImageUrl} alt={ext.Name} style={{ width: '48px', height: '48px', borderRadius: '8px', marginRight: '12px', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '48px', height: '48px', borderRadius: '8px', marginRight: '12px', backgroundColor: 'var(--color-surface-hover)' }} />
                      )}
                      <div style={{ flex: 1 }}>
                        <strong style={{ color: 'var(--color-text)' }}>{ext.Name}</strong>
                        <div style={{ color: 'var(--color-muted)', fontSize: '12px', marginTop: '2px' }}>
                          by {ext.creatorName || ext.Creator}
                          {typeof ext.usageCount === 'number' && ext.usageCount > 0 && ` · ${ext.usageCount} use${ext.usageCount === 1 ? '' : 's'} (72h)`}
                        </div>
                      </div>
                      {isSelected && <FaCheck style={{ color: 'var(--color-accent-strong)', fontSize: '18px', marginLeft: '8px', verticalAlign: 'middle' }} />}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Free-text search — promote any extension, not just the trending ones */}
            <div style={{ position: 'relative', marginTop: '10px' }}>
              <input
                type="text"
                value={extensionSearchQuery}
                onChange={(e) => setExtensionSearchQuery(e.target.value)}
                placeholder="Or search for any lens by name…"
                style={inputStyle}
              />
              {extensionSearchResults.length > 0 && (
                <div style={dropdownStyle}>
                  {extensionSearchResults.map(ext => (
                    <div
                      key={ext._id}
                      onClick={() => selectPromotionExtension(ext)}
                      style={dropdownItemStyle}
                    >
                      {ext.thumbnailImageUrl ? (
                        <img src={ext.thumbnailImageUrl} alt={ext.Name} style={{ width: '40px', height: '40px', borderRadius: '8px', marginRight: '12px', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '40px', height: '40px', borderRadius: '8px', marginRight: '12px', backgroundColor: 'var(--color-surface-hover)' }} />
                      )}
                      <div style={{ flex: 1 }}>
                        <strong>{ext.Name}</strong>
                        <div style={{ color: 'var(--color-muted)', fontSize: '12px', marginTop: '2px' }}>by {ext.creatorName || ext.Creator}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirms the current pick — a searched extension won't be highlighted in the list above */}
            {selectedExtension && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginTop: '10px',
                padding: '8px 10px',
                backgroundColor: 'var(--color-accent-surface)',
                border: '1px solid var(--color-accent-border)',
                borderRadius: '6px',
              }}>
                {selectedExtension.thumbnailImageUrl ? (
                  <img src={selectedExtension.thumbnailImageUrl} alt={selectedExtension.Name} style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '36px', height: '36px', borderRadius: '6px', backgroundColor: 'var(--color-surface-hover)' }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--color-muted)', fontSize: '11px' }}>Selected</div>
                  <strong style={{ color: 'var(--color-text)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedExtension.Name}</strong>
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedExtension(null); setExtensionId(''); }}
                  style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 4px' }}
                  title="Clear selection"
                >
                  <FaTimes />
                </button>
              </div>
            )}
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
              Push Notification Title
            </label>
            <input
              type="text"
              aria-label="Push notification title"
              maxLength={120}
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              placeholder="e.g. Trending on FRAW ✨"
              style={inputStyle}
            />
            <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>Defaults to "New Message" if empty</span>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
              Message *
            </label>
            <textarea
              aria-label="Notification message"
              maxLength={2000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Select a lens above to prefill a message you can edit…"
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
            />
          </div>
          {renderAudienceSelector()}

          {recipientMode !== 'csv' && (
          <div style={{ padding: '10px 12px', backgroundColor: 'var(--color-input)', borderRadius: '4px', marginBottom: '16px', fontSize: '13px', color: 'var(--color-muted)' }}>
            {recipientMode === 'specific' ? (
              <>
                This will send to the <strong style={{ color: 'var(--color-text)' }}>{selectedRecipients.length} selected user{selectedRecipients.length === 1 ? '' : 's'}</strong> (eligibility checked in preview). Tapping the notification opens the selected lens in the app.
              </>
            ) : (
              <>
                Recipients are checked against their featured-lens notification preference in the preview. Tapping the notification opens the selected lens in the app.
              </>
            )}
          </div>
          )}
        </>
      );
    }

    // Article: pick a published article to deep-link + broadcast/target
    if (formType === 'article') {
      return (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
              Article to Promote * <span style={{ fontWeight: 400, color: 'var(--color-muted)' }}>(tapping the notification opens it in the app)</span>
            </label>
            <input
              type="text"
              value={articleSearchQuery}
              onChange={(e) => setArticleSearchQuery(e.target.value)}
              placeholder="Search articles by title…"
              style={inputStyle}
            />
            {articlesLoading ? (
              <div style={{ color: 'var(--color-muted)', fontSize: '13px', padding: '12px 0' }}>Loading articles…</div>
            ) : articles.length === 0 ? (
              <div style={{ color: 'var(--color-muted)', fontSize: '13px', padding: '12px 0' }}>No published articles found.</div>
            ) : (
              <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '6px', marginTop: '8px' }}>
                {articles.map((a, idx) => {
                  const isSelected = selectedArticle?.articleId === a.articleId;
                  return (
                    <div
                      key={a.articleId}
                      onClick={() => selectArticle(a)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: idx < articles.length - 1 ? '1px solid var(--color-surface-hover)' : 'none',
                        backgroundColor: isSelected ? 'var(--color-accent-surface)' : 'transparent',
                      }}
                    >
                      {a.thumbnailUrl ? (
                        <img src={a.thumbnailUrl} alt={a.title} style={{ width: '48px', height: '48px', borderRadius: '8px', marginRight: '12px', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '48px', height: '48px', borderRadius: '8px', marginRight: '12px', backgroundColor: 'var(--color-surface-hover)' }} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ color: 'var(--color-text)' }}>{a.title}</strong>
                      </div>
                      {isSelected && <FaCheck style={{ color: 'var(--color-accent-strong)', fontSize: '18px', marginLeft: '8px', verticalAlign: 'middle' }} />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
              Push Notification Title
            </label>
            <input
              type="text"
              aria-label="Push notification title"
              maxLength={120}
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              placeholder="e.g. New on FRAW 📖"
              style={inputStyle}
            />
            <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>Defaults to "New Message" if empty</span>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
              Message *
            </label>
            <textarea
              aria-label="Notification message"
              maxLength={2000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Select an article above to prefill a message you can edit…"
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
            />
          </div>

          {renderAudienceSelector()}

          {recipientMode !== 'csv' && (
          <div style={{ padding: '10px 12px', backgroundColor: 'var(--color-input)', borderRadius: '4px', marginBottom: '16px', fontSize: '13px', color: 'var(--color-muted)' }}>
            {recipientMode === 'specific' ? (
              <>This will send to the <strong style={{ color: 'var(--color-text)' }}>{selectedRecipients.length} selected user{selectedRecipients.length === 1 ? '' : 's'}</strong>. Tapping the notification opens the article in the app.</>
            ) : (
              <>The preview will show the selected audience and eligible devices. Tapping the notification opens the article in the app.</>
            )}
          </div>
          )}
        </>
      );
    }

    // Broadcast: just title + message
    if (formType === 'broadcast' || formType === 'custom_message') {
      return (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
              Push Notification Title
            </label>
            <input
              type="text"
              aria-label="Push notification title"
              maxLength={120}
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              placeholder="e.g. Special Offer"
              style={inputStyle}
            />
            <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>Defaults to "New Message" if empty</span>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
              Message *
            </label>
            <textarea
              aria-label="Notification message"
              maxLength={2000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter the notification message..."
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
            />
          </div>

          {renderAudienceSelector()}

          {recipientMode !== 'csv' && (
          <div style={{ padding: '10px 12px', backgroundColor: 'var(--color-input)', borderRadius: '4px', marginBottom: '16px', fontSize: '13px', color: 'var(--color-muted)' }}>
            {recipientMode === 'specific' ? (
              <>
                This will send to the <strong style={{ color: 'var(--color-text)' }}>{selectedRecipients.length} selected user{selectedRecipients.length === 1 ? '' : 's'}</strong> (eligibility checked in preview).
              </>
            ) : (
              <>
                Recipients are checked against their promotion notification preference in the preview.
              </>
            )}
          </div>
          )}
        </>
      );
    }

    switch (formType) {
      case 'new_follower':
        return (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
              Actor (Follower) *
            </label>
            <input
              type="text"
              value={selectedActor ? selectedActor.name : actorSearchQuery}
              onChange={(e) => {
                setActorSearchQuery(e.target.value);
                setSelectedActor(null);
                setActorId('');
              }}
              placeholder="Search for actor..."
              style={inputStyle}
            />
            {actorSearchResults.length > 0 && !selectedActor && (
              <div style={dropdownStyle}>
                {actorSearchResults.map(user => (
                  <div
                    key={user.userId}
                    onClick={() => {
                      setSelectedActor(user);
                      setActorId(user.userId);
                      setActorSearchResults([]);
                    }}
                    style={dropdownItemStyle}
                  >
                    {user.avatar && (
                      <img src={user.avatar} alt={user.name} style={{ width: '32px', height: '32px', borderRadius: '50%', marginRight: '12px', objectFit: 'cover' }} />
                    )}
                    <div style={{ flex: 1 }}>
                      <strong>{user.name}</strong>
                      {user.email && <span style={{ color: 'var(--color-muted)', marginLeft: '8px' }}>{user.email}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'new_extension':
      case 'extension_recommendation':
        return (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
              Lens *
            </label>
            <input
              type="text"
              value={selectedExtension ? selectedExtension.Name : extensionSearchQuery}
              onChange={(e) => {
                setExtensionSearchQuery(e.target.value);
                setSelectedExtension(null);
                setExtensionId('');
              }}
              placeholder="Search for lens..."
              style={inputStyle}
            />
            {extensionSearchResults.length > 0 && !selectedExtension && (
              <div style={dropdownStyle}>
                {extensionSearchResults.map(ext => (
                  <div
                    key={ext._id}
                    onClick={() => {
                      setSelectedExtension(ext);
                      setExtensionId(ext._id);
                      setExtensionSearchResults([]);
                    }}
                    style={dropdownItemStyle}
                  >
                    {ext.thumbnailImageUrl && (
                      <img src={ext.thumbnailImageUrl} alt={ext.Name} style={{ width: '48px', height: '48px', borderRadius: '8px', marginRight: '12px', objectFit: 'cover' }} />
                    )}
                    <div style={{ flex: 1 }}>
                      <strong>{ext.Name}</strong>
                      <div style={{ color: 'var(--color-muted)', fontSize: '12px', marginTop: '2px' }}>by {ext.creatorName || ext.Creator}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'user_suggestion':
        return (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
              Suggested User *
            </label>
            <input
              type="text"
              value={selectedActor ? selectedActor.name : actorSearchQuery}
              onChange={(e) => {
                setActorSearchQuery(e.target.value);
                setSelectedActor(null);
                setActorId('');
              }}
              placeholder="Search for user to suggest..."
              style={inputStyle}
            />
            {actorSearchResults.length > 0 && !selectedActor && (
              <div style={dropdownStyle}>
                {actorSearchResults.map(user => (
                  <div
                    key={user.userId}
                    onClick={() => {
                      setSelectedActor(user);
                      setActorId(user.userId);
                      setActorSearchResults([]);
                    }}
                    style={dropdownItemStyle}
                  >
                    {user.avatar && (
                      <img src={user.avatar} alt={user.name} style={{ width: '32px', height: '32px', borderRadius: '50%', marginRight: '12px', objectFit: 'cover' }} />
                    )}
                    <div style={{ flex: 1 }}>
                      <strong>{user.name}</strong>
                      {user.email && <span style={{ color: 'var(--color-muted)', marginLeft: '8px' }}>{user.email}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading && notifications.length === 0) {
    return (
      <div className="page-status">Loading notifications...</div>
    );
  }

  // Engagement summary for extension-linked broadcasts (promotions and
  // new-extension fan-outs): how many recipients interacted (tapped / read) and
  // how many used the extension within 24h after reading. Other rows and
  // per-recipient child rows show "-".
  const renderEngagement = (notification: Notification): React.ReactNode => {
    if (!isPromotionRow(notification) || !notification.broadcastId) {
      return <span style={{ color: 'var(--color-subtle)' }}>-</span>;
    }
    const eng = engagement[notification.broadcastId];
    if (!eng) return <span style={{ color: 'var(--color-muted)' }}>…</span>;
    const dim: React.CSSProperties = { color: 'var(--color-muted)', fontWeight: 400 };
    return (
      <div style={{ fontSize: '12px', lineHeight: 1.6, color: 'var(--color-text)', minWidth: '150px' }}>
        <div title="Recipients who opened / read the notification">
          <FaEye size={12} style={{ verticalAlign: 'middle' }} /> Read: <b>{eng.readCount}</b> <span style={dim}>({formatPct(eng.readRate)})</span>
        </div>
        <div title="Recipients who actively tapped the notification">
          <FaHandPointer size={12} style={{ verticalAlign: 'middle' }} /> Tapped: <b>{eng.interactedCount}</b> <span style={dim}>({formatPct(eng.interactionRate)})</span>
        </div>
        <div title="Recipients who used the lens within 24h after reading">
          <FaMagic size={12} style={{ verticalAlign: 'middle' }} /> Used ext.: <b>{eng.usedCount}</b> <span style={dim}>({formatPct(eng.usageRate)})</span>
        </div>
      </div>
    );
  };

  // Unified content cell: the title + message the notification was actually sent
  // with, replacing the old Actor / Extension / Creator / Message columns (whose
  // values are already spelled out by the per-type copy). A follow is the one
  // case the copy alone doesn't place — "X started following you" never names the
  // recipient — so it gets a follower → followed line underneath.
  const renderContent = (notification: Notification): React.ReactNode => {
    const { title, message } = describeNotification(notification);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        {title && <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text)' }}>{title}</div>}
        {message && (
          <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {message}
          </div>
        )}
        {notification.campaign?.remark && <details style={{ fontSize: '12px', color: 'var(--color-muted)' }}><summary>Admin remark</summary><div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{notification.campaign.remark}</div></details>}
        {notification.type === 'new_follower' && (
          <div style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
            {notification.actorName || notification.actorId || 'Someone'}
            <span style={{ color: 'var(--color-subtle)' }}> → </span>
            {notification.userName || notification.userId}
          </div>
        )}
      </div>
    );
  };

  const renderDelivery = (campaign?: Campaign): React.ReactNode => {
    if (!campaign) return <span style={{ color: 'var(--color-muted)' }}>Not tracked (legacy / activity)</span>;
    const c = campaign.counts;
    const labels: Record<Campaign['status'], string> = {
      draft: 'Preview', processing: 'Processing', completed: 'Completed',
      partial_failure: 'Partially failed', failed: 'Failed', unknown: 'Outcome unknown',
    };
    return <div style={{ fontSize: '12px', lineHeight: 1.6, minWidth: '190px', color: 'var(--color-text)' }}>
      <strong>{labels[campaign.status]}</strong>
      <div>Provider accepted (devices): {c.acceptedTokens}</div>
      <div>Failed devices: {c.failedTokens} · Skipped users: {c.skippedUsers}</div>
      <div style={{ color: 'var(--color-muted)' }}>No active device: {c.noActiveDevice} · Preference off: {c.preferenceDisabled}</div>
      {campaign.error && <div role="alert">{campaign.error}</div>}
      <small>Provider acceptance does not confirm delivery or opening.</small>
    </div>;
  };

  const renderPreview = (campaign: Campaign): React.ReactNode => <div aria-label="Notification preview" style={{ color: 'var(--color-text)', lineHeight: 1.7 }}>
    <h3 style={{ marginTop: 0 }}>Review before sending</h3>
    <div>Push only · {getTypeLabel(campaign.campaignType)} · {campaign.audienceMode === 'all' ? 'All users' : campaign.audienceMode === 'specific' ? 'Selected users' : campaign.audienceMode === 'csv' ? 'CSV import' : `Inactive for ${campaign.inactiveDays}+ days`}</div>
    <div style={{ padding: '12px', border: '1px solid var(--color-border)', borderRadius: '6px', margin: '12px 0', overflowWrap: 'anywhere' }}>
      <strong>{campaign.title}</strong><p style={{ whiteSpace: 'pre-wrap' }}>{campaign.message}</p>
      <small>On tap: {campaign.target}</small>
    </div>
    {campaign.remark && <div style={{ padding: '10px', margin: '12px 0', border: '1px solid var(--color-border)', borderRadius: '4px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}><b>Remark</b><div>{campaign.remark}</div></div>}
    {campaign.csvImport && <p>CSV rows: {campaign.csvImport.rowCount} · Duplicate rows removed: {campaign.csvImport.duplicateRows}</p>}
    <div>Selected users: <b>{campaign.counts.selectedUsers}</b></div>
    {campaign.audienceMode === 'specific' && <div style={{ fontSize: '12px', overflowWrap: 'anywhere' }}>{selectedRecipients.map(u => `${u.name} (${u.userId})`).join(', ')}</div>}
    <div>Eligible users: <b>{campaign.counts.eligibleUsers}</b> · Devices: <b>{campaign.counts.eligibleTokens}</b></div>
    <div>Skipped users: <b>{campaign.counts.skippedUsers}</b> (no active device: {campaign.counts.noActiveDevice}; preference off: {campaign.counts.preferenceDisabled})</div>
    <p style={{ color: 'var(--color-muted)', fontSize: '12px' }}>This audience is fixed. Device eligibility is checked again when sending. Preview expires in 15 minutes.</p>
    {campaign.audienceMode === 'all' && <p role="alert">You are about to send to all eligible users in this audience.</p>}
  </div>;

  const renderNotificationRow = (notification: Notification) => (
    <tr key={notification._id} style={{ borderBottom: '1px solid var(--color-border)' }}>
      <td style={tdStyle}>
        <span style={{
          padding: '4px 8px',
          display: 'inline-block',
          whiteSpace: 'nowrap',
          borderRadius: '4px',
          backgroundColor: getTypeBadgeColor(notification.campaignType || notification.type),
          color: 'white',
          fontSize: '12px',
          fontWeight: '500'
        }}>
          {getTypeLabel(notification.campaignType || notification.type)}
        </span>
      </td>
      <td style={tdStyle}>
        {notification.broadcastId ? (
          <button
            onClick={() => openRecipientsModal(notification)}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', cursor: 'pointer', fontSize: '14px', textAlign: 'left' }}
            title="View recipients"
          >
            <FaUsers size={12} style={{ verticalAlign: 'middle' }} /> {notification.userName || 'Recipients'}
          </button>
        ) : (
          notification.userName || notification.userId.substring(0, 12) + '...'
        )}
      </td>
      <td style={{ ...tdStyle, minWidth: '220px', maxWidth: '460px' }}>{renderContent(notification)}</td>
      <td style={tdStyle}>{renderDelivery(notification.campaign)}</td>
      <td style={tdStyle}>{renderEngagement(notification)}</td>
      <td style={{ ...tdStyle, fontSize: '13px', color: 'var(--color-muted)' }}>
        {formatDate(notification.createdAt)}
      </td>
      <td style={tdStyle}>
        <button
          onClick={() => handleDelete(notification._id, notification.broadcastId)}
          style={{
            padding: '4px 8px',
            backgroundColor: 'var(--color-danger-strong)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Delete
        </button>
      </td>
    </tr>
  );

  const renderNotificationCard = (notification: Notification) => {
    return (
      <div
        key={notification._id}
        style={{
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '10px',
          padding: '14px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
            <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: getTypeBadgeColor(notification.campaignType || notification.type), color: 'white', fontSize: '12px', fontWeight: '500' }}>
              {getTypeLabel(notification.campaignType || notification.type)}
            </span>
          </div>
          <button
            onClick={() => handleDelete(notification._id, notification.broadcastId)}
            style={{ padding: '4px 10px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}
          >
            Delete
          </button>
        </div>
        <div style={{ fontSize: '13px', color: 'var(--color-text)' }}>
          <span style={{ color: 'var(--color-subtle)' }}>To: </span>
          {notification.broadcastId ? (
            <button
              onClick={() => openRecipientsModal(notification)}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', cursor: 'pointer', fontSize: '13px' }}
            >
              <FaUsers size={12} style={{ verticalAlign: 'middle' }} /> {notification.userName || 'Recipients'}
            </button>
          ) : (
            notification.userName || notification.userId.substring(0, 12) + '...'
          )}
        </div>
        <div style={{ backgroundColor: 'var(--color-input)', borderRadius: '6px', padding: '8px 10px' }}>
          {renderContent(notification)}
        </div>
        {notification.campaign && renderDelivery(notification.campaign)}
        {isPromotionRow(notification) && (
          <div style={{ backgroundColor: 'var(--color-input)', borderRadius: '6px', padding: '8px 10px' }}>
            {renderEngagement(notification)}
          </div>
        )}
        <div style={{ fontSize: '12px', color: 'var(--color-muted)' }}>{formatDate(notification.createdAt)}</div>
      </div>
    );
  };

  return (
    <div className="page">
      <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: 'var(--color-text)' }}>Notifications</h1>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-border)',
              background: 'var(--color-input)',
              color: 'var(--color-text)',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="all">All Types</option>
            {NOTIFICATION_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          <button
            onClick={() => {
              resetForm();
              setComposerMode('demo');
              setFormType(DEMO_NOTIFICATION_TYPES[0].value);
              setShowModal(true);
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#8957e5',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Trigger Demo
          </button>

          <button
            onClick={() => {
              resetForm();
              setComposerMode('standard');
              setFormType('broadcast');
              setShowModal(true);
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--color-success-strong)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            + Add Notification
          </button>
        </div>
      </div>

      {sendResult && (
        <div role="status" style={{ padding: '16px', marginBottom: '16px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
          <button aria-label="Dismiss send result" onClick={() => setSendResult(null)} style={{ float: 'right' }}>×</button>
          {renderDelivery(sendResult)}
        </div>
      )}
      {error && !showModal && (
        <div style={{
          padding: '12px',
          backgroundColor: 'var(--color-danger-surface)',
          border: '1px solid var(--color-danger-strong)',
          color: 'var(--color-danger)',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)' }}><FaTimes style={{ verticalAlign: 'middle' }} /></button>
        </div>
      )}

      {pagination && (
        <div style={{ marginBottom: '16px', color: 'var(--color-muted)', fontSize: '14px' }}>
          Showing {notifications.length} of {pagination.total} notifications
        </div>
      )}

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {notifications.map((notification) => renderNotificationCard(notification))}
        </div>
      ) : (
      <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflowX: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: 'var(--color-surface-hover)' }}>
            <tr>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Target User</th>
              <th style={thStyle}>Notification</th>
              <th style={thStyle}>Push result</th>
              <th style={thStyle}>Engagement</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {notifications.map((notification) => renderNotificationRow(notification))}
          </tbody>
        </table>
      </div>
      )}

      {pagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.pages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}

      {showModal && (
        <div style={modalOverlayStyle}>
          <div ref={composerRef} style={modalStyle} role="dialog" aria-modal="true" aria-label={preview ? 'Review notification' : 'Compose notification'}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'var(--color-text)' }}>
                {composerMode === 'demo' ? 'Trigger Demo Notification' : 'Add Notification'}
              </h2>
              <button
                aria-label="Close notification composer"
                disabled={submitting}
                onClick={() => { setShowModal(false); resetForm(); }}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--color-muted)' }}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {preview ? renderPreview(preview) : <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
                  Notification Type *
                </label>
                <select
                  aria-label="Notification type"
                  value={formType}
                  onChange={(e) => {
                    const newType = e.target.value as ComposerType;
                    resetForm();
                    setFormType(newType);
                    if (newType === 'extension_promotion') {
                      fetchTrendingExtensions();
                    }
                  }}
                  style={inputStyle}
                >
                  {(composerMode === 'demo' ? DEMO_NOTIFICATION_TYPES : COMPOSER_TYPES).map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {renderTypeFields()}
              {composerMode === 'standard' && <label style={{ display: 'block', marginBottom: '16px', color: 'var(--color-text)', fontSize: '14px' }}>
                Remark
                <textarea aria-label="Remark" value={remark} maxLength={1000} onChange={e => setRemark(e.target.value)} rows={3} placeholder="Internal campaign note, purpose or reference…" style={{ ...inputStyle, resize: 'vertical', marginTop: '8px' }} />
                <small style={{ color: 'var(--color-muted)' }}>{remark.length}/1000</small>
              </label>}

              {/* Activity/demo types target one user. Campaigns use the audience selector. */}
              {!STANDARD_NOTIFICATION_TYPES.some(option => option.value === formType) && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
                  Target User (who receives the notification) *
                </label>
                <input
                  type="text"
                  value={selectedUser ? selectedUser.name : userSearchQuery}
                  onChange={(e) => {
                    setUserSearchQuery(e.target.value);
                    setSelectedUser(null);
                    setTargetUserId('');
                  }}
                  placeholder="Search for user..."
                  style={inputStyle}
                />
                {userSearchResults.length > 0 && !selectedUser && (
                  <div style={dropdownStyle}>
                    {userSearchResults.map(user => (
                      <div
                        key={user.userId}
                        onClick={() => {
                          setSelectedUser(user);
                          setTargetUserId(user.userId);
                          setUserSearchResults([]);
                        }}
                        style={dropdownItemStyle}
                      >
                        {user.avatar && (
                          <img src={user.avatar} alt={user.name} style={{ width: '32px', height: '32px', borderRadius: '50%', marginRight: '12px', objectFit: 'cover' }} />
                        )}
                        <div style={{ flex: 1 }}>
                          <strong>{user.name}</strong>
                          {user.email && <span style={{ color: 'var(--color-muted)', marginLeft: '8px' }}>{user.email}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              </>}
              {sendAttempted && <p style={{ color: 'var(--color-muted)' }}>This campaign may already be sending. Checking again uses the same campaign and will not repeat the push.</p>}
              {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => { setShowModal(false); resetForm(); }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'var(--color-surface-hover)',
                    color: 'var(--color-text)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                {preview && !sendAttempted && (
                  <button type="button" disabled={submitting} onClick={() => {
                    setPreview(null); setError(null);
                    // The CSV widget remounts when returning from preview. Its
                    // empty upload state must not retain an invisible audience.
                    if (recipientMode === 'csv') setCsvReport(null);
                  }} style={{ ...inputStyle, width: 'auto' }}>Back to edit</button>
                )}
                <button type="submit" disabled={submitting || (recipientMode === 'csv' && csvBusy)}
                  style={{ padding: '10px 20px', backgroundColor: submitting ? 'var(--color-subtle)' : 'var(--color-accent-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
                  {csvBusy && recipientMode === 'csv' ? 'Validating CSV…' : submitting ? (preview ? 'Sending…' : 'Preparing…') : composerMode === 'demo' ? 'Send Demo Notification' : preview ? (sendAttempted ? 'Check / resume result' : 'Confirm and send') : 'Preview notification'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {recipientsBroadcast && (
        <div style={modalOverlayStyle} onClick={closeRecipientsModal}>
          <div style={recipientsModalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', gap: '12px' }}>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: '20px', color: 'var(--color-text)' }}>Recipients</h2>
                <div style={{ fontSize: '13px', color: 'var(--color-muted)', marginTop: '4px' }}>
                  {recipientsMeta?.isExtensionPromotion && recipientsMeta.extensionName ? (
                    <>{getTypeLabel(recipientsBroadcast.campaignType || recipientsBroadcast.type)} · <strong style={{ color: 'var(--color-text)' }}>{recipientsMeta.extensionName}</strong></>
                  ) : (
                    getTypeLabel(recipientsBroadcast.campaignType || recipientsBroadcast.type)
                  )}
                  {recipientsPagination && <> · {recipientsPagination.total} shown</>}
                  {recipientsMeta?.capped && recipientsPagination && recipientsMeta.totalRecipients > recipientsPagination.total && (
                    <> of {recipientsMeta.totalRecipients} (top {recipientsPagination.total} by engagement)</>
                  )}
                </div>
              </div>
              <button
                onClick={closeRecipientsModal}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--color-muted)', lineHeight: 1 }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Aggregate engagement (reuses the row's already-fetched campaign stats) */}
            {recipientsMeta?.isExtensionPromotion && recipientsBroadcast.broadcastId && engagement[recipientsBroadcast.broadcastId] && (
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', padding: '10px 12px', backgroundColor: 'var(--color-input)', borderRadius: '6px', marginBottom: '14px', fontSize: '13px', color: 'var(--color-text)' }}>
                <span title="Read the notification"><FaEye size={12} style={{ verticalAlign: 'middle' }} /> Read <b>{engagement[recipientsBroadcast.broadcastId].readCount}</b></span>
                <span title="Tapped the notification"><FaHandPointer size={12} style={{ verticalAlign: 'middle' }} /> Tapped <b>{engagement[recipientsBroadcast.broadcastId].interactedCount}</b></span>
                <span title="Used the lens within 24h after reading"><FaMagic size={12} style={{ verticalAlign: 'middle' }} /> Used <b>{engagement[recipientsBroadcast.broadcastId].usedCount}</b></span>
              </div>
            )}

            {recipientsMeta && (
              <div style={{ fontSize: '12px', color: 'var(--color-subtle)', marginBottom: '12px' }}>
                {recipientsMeta.isExtensionPromotion
                  ? 'Sorted by engagement: used the lens → tapped → read → the rest.'
                  : 'Sorted by name.'}
              </div>
            )}

            {recipientsLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>Loading recipients…</div>
            ) : recipients.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>No recipients found.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                {recipients.map((r) => (
                  <div
                    key={r.userId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px',
                      backgroundColor: 'var(--color-input)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      minWidth: 0,
                    }}
                  >
                    {r.avatarUrl ? (
                      <img src={r.avatarUrl} alt={r.userName} style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: 'var(--color-surface-hover)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)', fontSize: '14px' }}>
                        {r.userName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: 'var(--color-text)', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.userName}>
                        {r.userName}
                      </div>
                      <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                        {recipientsMeta?.isExtensionPromotion && r.used && <span style={recipientBadge('#8957e5')}><FaMagic size={9} style={{ verticalAlign: 'middle' }} /> Used</span>}
                        {r.tapped && <span style={recipientBadge('var(--color-accent-strong)')}><FaHandPointer size={9} style={{ verticalAlign: 'middle' }} /> Tapped</span>}
                        {r.isRead ? <span style={recipientBadge('var(--color-success-strong)')}><FaEye size={9} style={{ verticalAlign: 'middle' }} /> Read</span> : <span style={recipientBadge('var(--color-subtle)')}>Unread</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {recipientsPagination && recipientsPagination.pages > 1 && (
              <div style={{ marginTop: '16px' }}>
                <Pagination
                  currentPage={recipientsPage}
                  totalPages={recipientsPagination.pages}
                  onPageChange={changeRecipientsPage}
                />
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '12px',
  textAlign: 'left',
  fontWeight: '600',
  color: 'var(--color-text)',
  borderBottom: '2px solid var(--color-border)'
};

const tdStyle: React.CSSProperties = {
  padding: '12px',
  color: 'var(--color-text)'
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--color-border)',
  background: 'var(--color-input)',
  color: 'var(--color-text)',
  borderRadius: '4px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  width: 'calc(100% - 48px)',
  maxHeight: '200px',
  overflowY: 'auto',
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
  zIndex: 1000
};

const dropdownItemStyle: React.CSSProperties = {
  padding: '10px 12px',
  cursor: 'pointer',
  borderBottom: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  display: 'flex',
  alignItems: 'center'
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  borderRadius: '8px',
  padding: '24px',
  width: '548px',
  boxSizing: 'border-box',
  maxWidth: '94vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  position: 'relative'
};

const recipientsModalStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  borderRadius: '8px',
  padding: '24px',
  width: '760px',
  maxWidth: '92vw',
  maxHeight: '88vh',
  overflowY: 'auto',
  position: 'relative'
};

const recipientBadge = (bg: string): React.CSSProperties => ({
  padding: '1px 6px',
  borderRadius: '4px',
  backgroundColor: bg,
  color: '#fff',
  fontSize: '10px',
  fontWeight: 500,
});

